import { I18n } from "@assets/strings";
import React from "react";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { useCurrentProfile } from "@domain/currentProfile/hooks";
import { Article } from "@domain/lms/article/article";
import { useArticleFeedbacks } from "@domain/lms/article/hooks";
import { useIsMobile } from "@utils/hooks";
import { SecondaryTouchButton } from "@ui/components/buttons/primaryButton";
import { Secondary } from "@ui/components/containers/textBlocks";
import { Row } from "@ui/components/layout";
import { useModalTools } from "@ui/modals/modalContext";
import { gap } from "@ui/styles/utils";
import { FeedbackModal } from "./feedbackModal";
import { FeedbackView } from "./feedbackView";
interface ArticleFeedbacksProps extends React.HTMLAttributes<HTMLDivElement> {
	article: Article;
}
export const ArticleFeedbacks: React.FC<ArticleFeedbacksProps> = ({ article, ...htmlProps }) => {
	const { result: feedbacks, loading, fetchMore, totalElements } = useArticleFeedbacks(article.id);

	const { createModal } = useModalTools();
	const currentProfile = useCurrentProfile();
	const isMobile = useIsMobile();
	return (
		<Container {...htmlProps} title={I18n.t("article.comments")}>
			{isMobile ? (
				<>
					<Row>
						<StyledText>{`${I18n.t("article.comments")} (${feedbacks ? feedbacks.length : "0"})`}</StyledText>
					</Row>
					<Row style={{ marginTop: 15 }}>
						{currentProfile && (
							<SecondaryTouchButton
								onClick={() => {
									createModal((close) => <FeedbackModal close={close} article={article} />);
								}}
							>
								{I18n.t("article.add_comment")}
							</SecondaryTouchButton>
						)}
					</Row>
				</>
			) : (
				<CommentSection>
					<StyledText>{`${I18n.t("article.comments")} (${feedbacks ? feedbacks.length : "0"})`}</StyledText>
					<First>
						{currentProfile && (
							<SecondaryTouchButton
								onClick={() => {
									createModal((close) => <FeedbackModal close={close} article={article} />);
								}}
							>
								{I18n.t("article.add_comment")}
							</SecondaryTouchButton>
						)}
					</First>
				</CommentSection>
			)}

			<Feedbacks>
				{feedbacks.map((feedback) => (
					<FeedbackView key={feedback.user.id} feedback={feedback} article={article} />
				))}
			</Feedbacks>
			{loading ? (
				<ClipLoader />
			) : totalElements === 0 ? (
				<Secondary>{I18n.t("article.feedbacks.empty")}</Secondary>
			) : (
				totalElements &&
				totalElements > feedbacks.length && <FetchMore onClick={fetchMore}>{I18n.t("post.comment.more")}</FetchMore>
			)}
		</Container>
	);
};

const Container = styled.div`
	margin-top: 50px;
	display: flex;
	flex-direction: column;
	align-items: center;
	position: relative;
	margin-bottom: 0 !important;
`;

const CommentSection = styled.div`
	width: 100%;
	padding: 15px;
	margin-bottom: 30px;
	background: #ffffff;
	border: 2px solid rgba(31, 106, 247, 0.5);
	box-sizing: border-box;
	border-radius: 10px;
	${gap(28)}

	display: flex;
	align-items: center;
	justify-content: space-between;

	@media (max-width: 900px) {
		${gap(15)}
	}
`;
const Feedbacks = styled.div`
	width: 100%;

	${gap(28)}
	@media (max-width: 900px) {
		${gap(15)}
		padding: 15px;
	}
`;

const StyledText = styled.div`
	font-family: Sora;
	font-style: normal;
	font-weight: 600;
	padding-top: 20px;
	padding-left: 20px;
	font-size: 20px;
	line-height: 18px;
	/* identical to box height */

	color: #000000;
`;

const FetchMore = styled.div`
	margin-top: 44px;
	cursor: pointer;
	${({ theme }) => theme.textStyles.tertiary};
`;

const First = styled.div`
	display: flex;
	align-items: center;
	${gap(6, "row")}
	svg {
		width: 18px;
	}
	@media (max-width: 900px) {
		align-self: flex-start;
	}
`;
